import React, { useState } from 'react';
import { SongAdScene } from '../../services/api/songAdService';
import { SongAdSceneDraft } from './types';
import { TaskyButton, TaskyTextarea, TaskyEmptyState, TaskyAlert, TaskyDialog, TaskyTooltip, useConfirm } from '../tasky';
import { ProductImageSelectionModal } from '../image-generation';
import {
  ImageIcon,
  RefreshCw,
  Loader2,
  AlertCircle,
  Sparkles,
  Save,
  GripVertical,
  Plus,
  Trash2,
  FolderOpen,
  Wand2,
  ArrowRight,
} from 'lucide-react';

/**
 * ステップ③: ストーリーボード。
 *
 * シーン未生成の場合はまず歌詞からシーン分割（generate-scenes）を行う。
 * シーンごとに開始フレーム（静止画）を生成し、割り当てられた歌詞行はここで編集できる。
 *
 * v3で追加: HTML5ネイティブDnDでのカード並び替え（ドロップ時に即PUT scenes）・
 * シーン追加/削除・素材差し替え（商品画像から選択）・シーン単位AI修正（instruction）・失敗時の再試行。
 * 歌詞行のテキスト編集は従来どおりローカルドラフト+「設定を保存」でまとめてPUTする。
 */

interface StoryboardStepProps {
  scenes: SongAdScene[];
  sceneDrafts: SongAdSceneDraft[];
  aspectRatio: string;
  generatingSceneIds: Set<string>;
  canGenerateScenes: boolean;
  isGeneratingScenes: boolean;
  isSavingDrafts: boolean;
  isReordering?: boolean;
  onGenerateScenes: () => Promise<void>;
  onGenerate: (sceneId: string) => void;
  onGenerateAll: () => void;
  /** 一括生成の実行中。ボタンを無効化して連打による重複リクエストを防ぐ */
  isGeneratingAll?: boolean;
  /** 429（同時実行上限）直後のクールダウン中。生成ボタンをしばらく押せなくする */
  generationCoolingDown?: boolean;
  onSceneLyricLinesChange: (sceneId: string, text: string) => void;
  onSaveDrafts: () => Promise<void>;
  /** ドラッグ&ドロップで確定した新しい並び順（シーンid配列）。呼び出し側で即PUTする */
  onReorder: (orderedSceneIds: string[]) => void;
  onAddScene: () => void;
  onDeleteScene: (sceneId: string) => void;
  onAiEditScene: (sceneId: string, instruction: string) => Promise<void>;
  onReplaceSceneImage: (sceneId: string, s3Key: string) => Promise<void>;
  onProceedToMotion?: () => void;
}

const aspectClass = (ratio: string): string => {
  switch (ratio) {
    case '9:16':
      return 'aspect-[9/16]';
    case '1:1':
      return 'aspect-square';
    default:
      return 'aspect-video';
  }
};

const AI_EDIT_SUGGESTIONS = ['商品をもっと大きくする', '人物を削除する', '背景を明るくする', 'ブランドカラーを強める', '実写風にする', '画面をシンプルにする'];

const StoryboardStep: React.FC<StoryboardStepProps> = ({
  scenes,
  sceneDrafts,
  aspectRatio,
  generatingSceneIds,
  canGenerateScenes,
  isGeneratingScenes,
  isSavingDrafts,
  isReordering = false,
  onGenerateScenes,
  onGenerate,
  onGenerateAll,
  isGeneratingAll = false,
  generationCoolingDown = false,
  onSceneLyricLinesChange,
  onSaveDrafts,
  onReorder,
  onAddScene,
  onDeleteScene,
  onAiEditScene,
  onReplaceSceneImage,
  onProceedToMotion,
}) => {
  const confirm = useConfirm();
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [assetSwapSceneId, setAssetSwapSceneId] = useState<string | null>(null);
  const [aiEditSceneId, setAiEditSceneId] = useState<string | null>(null);
  const [aiInstruction, setAiInstruction] = useState('');
  const [isAiSubmitting, setIsAiSubmitting] = useState(false);

  if (scenes.length === 0) {
    return canGenerateScenes ? (
      <TaskyEmptyState
        icon={<ImageIcon />}
        title="シーンに分割してください"
        action={
          <TaskyButton onClick={onGenerateScenes} isLoading={isGeneratingScenes}>
            <Sparkles className="w-4 h-4" />
            シーンに分割する
          </TaskyButton>
        }
      />
    ) : (
      <TaskyEmptyState
        icon={<ImageIcon />}
        title="先に歌詞をフィックスしてください"
        description="「① 歌詞」でフィックスして保存するとシーンに分割できます"
      />
    );
  }

  const pendingScenes = scenes.filter(
    (s) => s.storyboard.status === 'none' || s.storyboard.status === 'failed'
  );
  const activeCount = scenes.filter(
    (s) => s.storyboard.status === 'pending' || s.storyboard.status === 'processing'
  ).length;
  const allReady = scenes.every((s) => !!s.storyboard.image);

  const handleDrop = (targetId: string) => {
    setDragOverId(null);
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const order = scenes.map((s) => s.id);
    const from = order.indexOf(dragId);
    const to = order.indexOf(targetId);
    if (from < 0 || to < 0) {
      setDragId(null);
      return;
    }
    const next = [...order];
    next.splice(from, 1);
    next.splice(to, 0, dragId);
    setDragId(null);
    onReorder(next);
  };

  const aiEditScene = scenes.find((s) => s.id === aiEditSceneId);

  const handleAiEditSubmit = async () => {
    if (!aiEditSceneId || !aiInstruction.trim()) return;
    setIsAiSubmitting(true);
    try {
      await onAiEditScene(aiEditSceneId, aiInstruction.trim());
      setAiEditSceneId(null);
      setAiInstruction('');
    } finally {
      setIsAiSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-gray-600">
          歌詞行の編集は「設定を保存」で反映されます。
        </p>
        <div className="flex items-center gap-2">
          <TaskyButton variant="outline" size="sm" onClick={onAddScene} disabled={isReordering}>
            <Plus className="w-3.5 h-3.5" />
            シーンを追加
          </TaskyButton>
          <TaskyButton variant="outline" size="sm" onClick={() => onSaveDrafts()} isLoading={isSavingDrafts}>
            <Save className="w-3.5 h-3.5" />
            設定を保存
          </TaskyButton>
          <TaskyButton
            size="sm"
            onClick={onGenerateAll}
            isLoading={isGeneratingAll}
            disabled={pendingScenes.length === 0 || isGeneratingAll || generationCoolingDown}
          >
            {!isGeneratingAll && <Sparkles className="w-3.5 h-3.5" />}
            {isGeneratingAll ? '生成を開始しています…' : `未生成を一括生成（${pendingScenes.length}件）`}
          </TaskyButton>
        </div>
      </div>

      {activeCount > 0 && (
        <TaskyAlert tone="info" icon={<Loader2 className="w-5 h-5 text-primary animate-spin" />} className="mb-4">
          {activeCount}件の絵コンテを生成中です。完了すると自動で表示されます。
        </TaskyAlert>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {scenes.map((scene, index) => {
          const status = scene.storyboard.status;
          const isRequesting = generatingSceneIds.has(scene.id);
          const isActive = status === 'pending' || status === 'processing' || isRequesting;
          const draft = sceneDrafts.find((d) => d.id === scene.id);
          const isDragOver = dragOverId === scene.id && dragId !== scene.id;

          return (
            <div
              key={scene.id}
              draggable
              onDragStart={() => setDragId(scene.id)}
              onDragEnd={() => {
                setDragId(null);
                setDragOverId(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverId(scene.id);
              }}
              onDragLeave={() => setDragOverId((cur) => (cur === scene.id ? null : cur))}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(scene.id);
              }}
              className={`border rounded-xl overflow-hidden bg-white transition-shadow ${
                isDragOver ? 'border-primary ring-2 ring-primary/30' : 'border-warm-border'
              } ${dragId === scene.id ? 'opacity-50' : ''}`}
            >
              <div className={`${aspectClass(aspectRatio)} bg-gray-100 relative flex items-center justify-center`}>
                {scene.storyboard.image ? (
                  <img
                    src={scene.storyboard.image.url}
                    alt={`シーン${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : isActive ? (
                  <div className="flex flex-col items-center text-gray-400">
                    <Loader2 className="w-7 h-7 animate-spin mb-2" />
                    <span className="text-xs">生成中...</span>
                  </div>
                ) : status === 'failed' ? (
                  <div className="flex flex-col items-center text-red-400 px-4 text-center">
                    <AlertCircle className="w-7 h-7 mb-2" />
                    <span className="text-xs mb-2">{scene.storyboard.error_message ?? '生成に失敗しました'}</span>
                    <button
                      type="button"
                      onClick={() => onGenerate(scene.id)}
                      disabled={generationCoolingDown}
                      className="text-xs text-white bg-destructive hover:opacity-90 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50"
                    >
                      再試行
                    </button>
                  </div>
                ) : (
                  <ImageIcon className="w-9 h-9 text-gray-300" />
                )}
                <span className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 text-white text-[10px] font-semibold rounded-full px-2 py-0.5">
                  <GripVertical className="w-3 h-3 cursor-grab" />
                  シーン {index + 1}
                </span>
              </div>

              <div className="p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] text-gray-400">{scene.lyric_lines.length}行</span>
                  {/* v8: アイコンだけでは何のアクションか分からないという指摘への対応。
                      TaskyTooltip（150ms・視認性の高いカスタムツールチップ）でホバー説明を付ける。
                      title属性も残し、TaskyTooltip側に問題があった場合のネイティブフォールバックにする */}
                  <div className="flex items-center">
                    <TaskyTooltip content="画像を再生成">
                      <button
                        onClick={() => onGenerate(scene.id)}
                        disabled={isActive || !scene.storyboard_prompt || generationCoolingDown}
                        title="画像を再生成"
                        aria-label="画像を再生成"
                        className="p-1.5 text-gray-400 hover:text-primary transition-colors disabled:opacity-40"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isRequesting ? 'animate-spin' : ''}`} />
                      </button>
                    </TaskyTooltip>
                    <TaskyTooltip content="素材に差し替え">
                      <button
                        onClick={() => setAssetSwapSceneId(scene.id)}
                        disabled={isActive}
                        title="素材に差し替え"
                        className="p-1.5 text-gray-400 hover:text-primary transition-colors disabled:opacity-40"
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                      </button>
                    </TaskyTooltip>
                    <TaskyTooltip content="AIで修正">
                      <button
                        onClick={() => setAiEditSceneId(scene.id)}
                        disabled={isActive}
                        title="AIで修正"
                        className="p-1.5 text-gray-400 hover:text-primary transition-colors disabled:opacity-40"
                      >
                        <Wand2 className="w-3.5 h-3.5" />
                      </button>
                    </TaskyTooltip>
                    <TaskyTooltip content={scenes.length <= 3 ? 'シーンは3つ以上必要です' : '削除'}>
                      <button
                        onClick={async () => {
                          if (scenes.length <= 3) return;
                          if (await confirm({ title: `シーン${index + 1}を削除しますか？残りのシーンで尺を自動再配分します。`, tone: 'destructive', confirmText: '削除する' })) {
                            onDeleteScene(scene.id);
                          }
                        }}
                        disabled={scenes.length <= 3}
                        title={scenes.length <= 3 ? 'シーンは3つ以上必要です' : '削除'}
                        className="p-1.5 text-gray-400 hover:text-destructive transition-colors disabled:opacity-40"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </TaskyTooltip>
                  </div>
                </div>

                <label className="block text-[11px] text-gray-500 mb-0.5">このシーンで歌う歌詞行</label>
                <TaskyTextarea
                  value={draft?.lyricLinesText ?? ''}
                  onChange={(e) => onSceneLyricLinesChange(scene.id, e.target.value)}
                  rows={3}
                  className="text-xs"
                />

                {scene.storyboard_prompt && (
                  <details className="mt-1.5">
                    <summary className="text-[11px] text-gray-400 cursor-pointer select-none">絵コンテプロンプト</summary>
                    <p className="text-[11px] text-gray-500 mt-1 whitespace-pre-wrap">{scene.storyboard_prompt}</p>
                  </details>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {onProceedToMotion && (
        <div className="flex justify-end mt-5">
          <TaskyButton onClick={onProceedToMotion} disabled={!allReady}>
            モーション設定へ
            <ArrowRight className="w-4 h-4" />
          </TaskyButton>
        </div>
      )}

      {/* 素材差し替えモーダル */}
      {assetSwapSceneId && (
        <ProductImageSelectionModal
          isOpen
          maxSelection={1}
          onClose={() => setAssetSwapSceneId(null)}
          onSelect={(selected) => {
            const sceneId = assetSwapSceneId;
            setAssetSwapSceneId(null);
            if (sceneId && selected[0]) {
              void onReplaceSceneImage(sceneId, selected[0].s3Key);
            }
          }}
        />
      )}

      {/* AI修正モーダル */}
      <TaskyDialog
        open={!!aiEditSceneId}
        onClose={() => {
          setAiEditSceneId(null);
          setAiInstruction('');
        }}
        title={aiEditScene ? `AIでシーン${scenes.indexOf(aiEditScene) + 1}を修正` : 'AIで修正'}
        footer={
          <>
            <TaskyButton
              variant="outline"
              onClick={() => {
                setAiEditSceneId(null);
                setAiInstruction('');
              }}
            >
              キャンセル
            </TaskyButton>
            <TaskyButton onClick={handleAiEditSubmit} isLoading={isAiSubmitting} disabled={!aiInstruction.trim()}>
              <Wand2 className="w-4 h-4" />
              修正する
            </TaskyButton>
          </>
        }
      >
        <TaskyTextarea
          value={aiInstruction}
          onChange={(e) => setAiInstruction(e.target.value)}
          rows={2}
          placeholder="例：商品をもっと大きくして、背景を明るくする"
          className="mb-3"
        />
        <div className="flex flex-wrap gap-1.5">
          {AI_EDIT_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setAiInstruction((prev) => (prev ? `${prev}、${s}` : s))}
              className="rounded-full px-3 py-1.5 text-xs text-gray-600 border border-warm-border hover:border-primary transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      </TaskyDialog>
    </div>
  );
};

export default StoryboardStep;
